import { useInfiniteQuery } from '@tanstack/react-query';
import { Copy, Eye, Filter, Search, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Card from '../components/Card';
import Input from '../components/Input';
import Button from '../components/Button';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import { adminApi } from '../api/admin';
import type { AuditEvent } from '../types';
import Modal from '../components/Modal';

type Filters = {
  q: string;
  entity_type: string;
  entity_id: string;
  action: string;
  actor_type: string;
  actor_email: string;
  actor_user_id: string;
  job_id: string;
  series_id: string;
  series_title: string;
  chapter_id: string;
  after: string;
};

type Chip = { key: keyof Filters; label: string; value: string };

function cleanFilters(f: Filters): Partial<Filters> {
  const next: Partial<Filters> = {};
  for (const k of Object.keys(f) as (keyof Filters)[]) {
    const value = String(f[k] ?? '').trim();
    if (value) next[k] = value;
  }
  return next;
}

function formatDateTimeRu(value: string) {
  const d = new Date(value);
  const isValid = !Number.isNaN(d.getTime());
  if (!isValid) return value;
  return d.toLocaleString('ru-RU');
}

function actionTone(action: string): 'neutral' | 'info' | 'success' | 'warning' | 'danger' {
  const a = String(action || '');
  if (a.includes('deleted') || a.includes('removed') || a.includes('cancelled')) return 'danger';
  if (a.includes('failed') || a.includes('error')) return 'danger';
  if (a.includes('paused')) return 'warning';
  if (a.includes('resumed') || a.includes('retried') || a.includes('requeued')) return 'info';
  if (a.includes('created') || a.includes('uploaded') || a.includes('completed')) return 'success';
  if (a.includes('updated') || a.includes('changed')) return 'info';
  return 'neutral';
}

function toneClasses(tone: ReturnType<typeof actionTone>) {
  if (tone === 'danger') return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200 border-red-200 dark:border-red-900/40';
  if (tone === 'warning') return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-200 border-orange-200 dark:border-orange-900/40';
  if (tone === 'success') return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200 border-green-200 dark:border-green-900/40';
  if (tone === 'info') return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200 border-blue-200 dark:border-blue-900/40';
  return 'bg-gray-100 text-gray-800 dark:bg-gray-700/50 dark:text-gray-200 border-gray-200 dark:border-gray-700';
}

function prettyJson(value: unknown) {
  try {
    return JSON.stringify(value ?? null, null, 2);
  } catch {
    return String(value ?? '');
  }
}

export default function AuditPage() {
  const [draft, setDraft] = useState<Filters>({
    q: '',
    entity_type: '',
    entity_id: '',
    action: '',
    actor_type: '',
    actor_email: '',
    actor_user_id: '',
    job_id: '',
    series_id: '',
    series_title: '',
    chapter_id: '',
    after: '',
  });

  const [applied, setApplied] = useState<Filters>(draft);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<AuditEvent | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const params = useMemo(() => ({ limit: 200, ...cleanFilters(applied) }), [applied]);

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['admin', 'audit', params],
    queryFn: ({ pageParam }) => adminApi.audit.getAll({ ...params, before: pageParam ? String(pageParam) : undefined }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => {
      if (!lastPage || lastPage.length < Number(params.limit)) return undefined;
      const last = lastPage[lastPage.length - 1];
      return last?.created_at ? String(last.created_at) : undefined;
    },
  });

  const events = useMemo(() => (data?.pages || []).flat(), [data?.pages]);
  const visibleEvents = useMemo(() => events.filter((e) => e.action !== 'job.status_changed'), [events]);

  const chips = useMemo(() => {
    const f = applied;
    const list: Chip[] = [];
    const add = (key: keyof Filters, label: string, value: string) => {
      const v = String(value ?? '').trim();
      if (v) list.push({ key, label, value: v });
    };
    add('q', 'Поиск', f.q);
    add('after', 'Период', f.after);
    add('entity_type', 'Entity', f.entity_type);
    add('action', 'Action', f.action);
    add('actor_type', 'Actor', f.actor_type);
    add('actor_email', 'Почта', f.actor_email);
    add('series_title', 'Серия', f.series_title);
    add('job_id', 'Job', f.job_id);
    add('series_id', 'Series id', f.series_id);
    add('chapter_id', 'Chapter id', f.chapter_id);
    add('entity_id', 'Entity id', f.entity_id);
    add('actor_user_id', 'Actor user id', f.actor_user_id);
    return list;
  }, [applied]);

  const getActorLabel = (e: AuditEvent) => {
    if (e.actor_type === 'user') {
      return e.actor_email ? `${e.actor_email}${e.actor_role ? ` (${e.actor_role})` : ''}` : 'Пользователь';
    }
    if (e.actor_type === 'api_key') return 'API key';
    if (e.actor_type === 'system') return 'Система';
    return e.actor_type || '—';
  };

  const getAuditLabel = (e: AuditEvent) => {
    switch (e.action) {
      case 'job.created':
        return 'Создана задача';
      case 'job.created_from_upload':
        return 'Создана задача (загрузка архива)';
      case 'job.duplicate_upload_detected':
        return 'Дубликат загрузки (задача уже существует)';
      case 'job.requeued_from_duplicate_upload':
        return 'Перезапущена из-за дубликата загрузки';
      case 'job.retried':
        return 'Перезапуск';
      case 'job.paused':
        return 'Приостановлена';
      case 'job.resumed':
        return 'Возобновлена';
      case 'job.cancelled':
        return 'Отменена';
      case 'job.result_downloaded':
        return 'Скачан результат';
      case 'job.status_changed':
        return 'Изменение статуса';
      case 'job.worker_stopped':
        return 'Воркер остановил обработку';
      case 'chapter.created':
        return `Создана глава${e.chapter_no != null ? ` ${e.chapter_no}` : ''}`;
      case 'chapter.deleted':
        return `Удалена глава${e.chapter_no != null ? ` ${e.chapter_no}` : ''}`;
      case 'series.created':
        return 'Создана серия';
      case 'series.updated':
        return 'Изменена серия';
      case 'series.deleted':
        return 'Удалена серия';
      case 'series.duplicate_create_detected':
        return 'Дубликат создания серии (вернули существующую)';
      case 'team.created':
        return 'Создана команда';
      case 'team.updated':
        return 'Изменена команда';
      case 'team.deleted':
        return 'Удалена команда';
      case 'team.member_added':
        return 'Добавлен участник';
      case 'team.member_removed':
        return 'Удалён участник';
      case 'team.member_role_changed':
        return 'Изменена роль участника';
      default:
        return e.action.replace(/[._]/g, ' ').replace(/^\w/, c => c.toUpperCase());
    }
  };

  const updateDraft = (patch: Partial<Filters>) => setDraft((p) => ({ ...p, ...patch }));

  const setQuickRange = (range: '24h' | '7d' | '30d' | 'all') => {
    if (range === 'all') return updateDraft({ after: '' });
    const now = Date.now();
    const ms =
      range === '24h' ? 24 * 60 * 60 * 1000 : range === '7d' ? 7 * 24 * 60 * 60 * 1000 : 30 * 24 * 60 * 60 * 1000;
    const iso = new Date(now - ms).toISOString();
    updateDraft({ after: iso });
  };

  const clearAll = () => {
    const empty: Filters = {
      q: '',
      entity_type: '',
      entity_id: '',
      action: '',
      actor_type: '',
      actor_email: '',
      actor_user_id: '',
      job_id: '',
      series_id: '',
      series_title: '',
      chapter_id: '',
      after: '',
    };
    setDraft(empty);
    setApplied(empty);
  };

  const copyText = async (text: string) => {
    const value = String(text ?? '').trim();
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(value);
    } catch {
      setCopied(value);
    }
  };

  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(null), 1500);
    return () => window.clearTimeout(t);
  }, [copied]);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    if (!hasNextPage || isFetchingNextPage) return;

    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) fetchNextPage();
      },
      { rootMargin: '400px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, params]);

  const pill = (active: boolean) =>
    `inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors ${
      active
        ? 'bg-primary-600 text-white border-primary-600'
        : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700 dark:hover:bg-gray-700'
    }`;

  const getTargetLabel = (e: AuditEvent) => {
    if (e.series_title) return e.series_title;
    if (e.series_id) return `Series ${String(e.series_id).slice(0, 8)}…`;
    if (e.job_id) return `Job ${String(e.job_id).slice(0, 8)}…`;
    return `${e.entity_type}:${String(e.entity_id).slice(0, 12)}…`;
  };

  return (
    <div className="space-y-6">
      <div className="sticky top-16 z-30">
        <Card>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col lg:flex-row gap-3 lg:items-end">
              <div className="flex-1">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  <Search className="h-4 w-4" />
                  Поиск
                </div>
                <Input
                  placeholder="action, почта, серия, id…"
                  value={draft.q}
                  onChange={(e) => updateDraft({ q: e.target.value })}
                />
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className={pill(!draft.after)}
                  onClick={() => setQuickRange('all')}
                >
                  Все
                </button>
                <button
                  type="button"
                  className={pill(Boolean(draft.after) && new Date(draft.after).getTime() > Date.now() - 25 * 60 * 60 * 1000)}
                  onClick={() => setQuickRange('24h')}
                >
                  24ч
                </button>
                <button
                  type="button"
                  className={pill(Boolean(draft.after) && new Date(draft.after).getTime() > Date.now() - 8 * 24 * 60 * 60 * 1000)}
                  onClick={() => setQuickRange('7d')}
                >
                  7д
                </button>
                <button
                  type="button"
                  className={pill(Boolean(draft.after) && new Date(draft.after).getTime() > Date.now() - 31 * 24 * 60 * 60 * 1000)}
                  onClick={() => setQuickRange('30d')}
                >
                  30д
                </button>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap gap-2 items-center">
                <div className="text-sm text-gray-600 dark:text-gray-300 mr-1">Entity:</div>
                {['', 'job', 'chapter', 'series', 'team'].map((v) => (
                  <button
                    key={v || 'all'}
                    type="button"
                    className={pill(draft.entity_type === v)}
                    onClick={() => updateDraft({ entity_type: v })}
                  >
                    {v ? v : 'Любой'}
                  </button>
                ))}

                <div className="text-sm text-gray-600 dark:text-gray-300 ml-4 mr-1">Actor:</div>
                {['', 'user', 'api_key', 'system'].map((v) => (
                  <button
                    key={v || 'any'}
                    type="button"
                    className={pill(draft.actor_type === v)}
                    onClick={() => updateDraft({ actor_type: v })}
                  >
                    {v ? v : 'Любой'}
                  </button>
                ))}
              </div>

              <div className="flex flex-wrap gap-2 items-center">
                <Button
                  variant="primary"
                  onClick={() => {
                    setApplied(draft);
                  }}
                >
                  Применить
                </Button>
                <Button variant="secondary" onClick={clearAll}>
                  Сбросить
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setAdvancedOpen((v) => !v)}
                  className="inline-flex items-center gap-2"
                >
                  <Filter className="h-4 w-4" />
                  Расширенные
                </Button>
                <Button variant="secondary" onClick={() => refetch()}>
                  Обновить
                </Button>
                <div className="text-sm text-gray-500 dark:text-gray-300">
                  Показано: {events.length}
                  {copied ? <span className="ml-2 text-primary-600 dark:text-primary-300">Скопировано</span> : null}
                </div>
              </div>

              {chips.length ? (
                <div className="flex flex-wrap gap-2">
                  {chips.map((c) => (
                    <button
                      key={`${c.key}:${c.value}`}
                      type="button"
                      className="inline-flex items-center gap-2 rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-1 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                      onClick={() => {
                        const next = { ...draft, [c.key]: '' } as Filters;
                        setDraft(next);
                        setApplied((p) => ({ ...p, [c.key]: '' } as Filters));
                      }}
                      title="Убрать фильтр"
                    >
                      <span className="text-gray-500 dark:text-gray-300">{c.label}:</span>
                      <span className="max-w-[220px] truncate">{c.value}</span>
                      <X className="h-4 w-4 opacity-70" />
                    </button>
                  ))}
                </div>
              ) : null}

              {advancedOpen ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 pt-2">
                  <Input
                    label="Action содержит"
                    placeholder="job.created_from_upload"
                    value={draft.action}
                    onChange={(e) => updateDraft({ action: e.target.value })}
                  />
                  <Input
                    label="Почта содержит"
                    placeholder="user@example.com"
                    value={draft.actor_email}
                    onChange={(e) => updateDraft({ actor_email: e.target.value })}
                  />
                  <Input
                    label="Название серии содержит"
                    placeholder="Naruto"
                    value={draft.series_title}
                    onChange={(e) => updateDraft({ series_title: e.target.value })}
                  />
                  <Input
                    label="Job id"
                    placeholder="uuid или префикс"
                    value={draft.job_id}
                    onChange={(e) => updateDraft({ job_id: e.target.value })}
                  />
                  <Input
                    label="Series id"
                    placeholder="text id"
                    value={draft.series_id}
                    onChange={(e) => updateDraft({ series_id: e.target.value })}
                  />
                  <Input
                    label="Chapter id"
                    placeholder="uuid или префикс"
                    value={draft.chapter_id}
                    onChange={(e) => updateDraft({ chapter_id: e.target.value })}
                  />
                  <Input
                    label="Entity id"
                    placeholder="совпадение по entity_id"
                    value={draft.entity_id}
                    onChange={(e) => updateDraft({ entity_id: e.target.value })}
                  />
                  <Input
                    label="Actor user id"
                    placeholder="uuid или префикс"
                    value={draft.actor_user_id}
                    onChange={(e) => updateDraft({ actor_user_id: e.target.value })}
                  />
                  <Input
                    label="After (ISO)"
                    placeholder="2026-01-01T00:00:00.000Z"
                    value={draft.after}
                    onChange={(e) => updateDraft({ after: e.target.value })}
                  />
                </div>
              ) : null}
            </div>
          </div>
        </Card>
      </div>

      <Card title="События">
        {isLoading ? (
          <LoadingSpinner />
        ) : error ? (
          <ErrorMessage message={error instanceof Error && error.message ? error.message : 'Не удалось загрузить логи'} />
        ) : visibleEvents.length > 0 ? (
          <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    <th className="px-4 py-3">Время</th>
                    <th className="px-4 py-3">Действие</th>
                    <th className="px-4 py-3">Кто</th>
                    <th className="px-4 py-3">К чему</th>
                    <th className="px-4 py-3 w-[132px] text-right">Детали</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {visibleEvents.map((e) => {
                    const tone = actionTone(e.action);
                    const badge = toneClasses(tone);
                    const actor = getActorLabel(e);
                    const actorInitial =
                      (e.actor_email && String(e.actor_email).trim()[0]) || (e.actor_type && String(e.actor_type).trim()[0]) || '?';
                    const time = formatDateTimeRu(String(e.created_at));
                    return (
                      <tr key={e.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                        <td className="px-4 py-3 whitespace-nowrap text-gray-700 dark:text-gray-200">{time}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${badge}`}>
                              {getAuditLabel(e)}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3 min-w-[220px]">
                            <div className="h-8 w-8 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-gray-700 dark:text-gray-100 font-semibold">
                              {String(actorInitial).toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <div className="text-gray-900 dark:text-white truncate">{actor}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                {e.ip ? String(e.ip) : '—'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="min-w-[260px]">
                            <div className="text-gray-900 dark:text-white truncate" title={e.series_title || undefined}>
                              {e.series_id ? (
                                <Link to={`/series/${e.series_id}`} className="hover:underline">
                                  {getTargetLabel(e)}
                                </Link>
                              ) : e.job_id ? (
                                <Link to={`/jobs/${e.job_id}`} className="hover:underline">
                                  {getTargetLabel(e)}
                                </Link>
                              ) : (
                                <span>{getTargetLabel(e)}</span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 font-mono truncate">
                              {e.job_id ? `job:${String(e.job_id).slice(0, 12)}…` : null}
                              {e.series_id ? ` • series:${String(e.series_id).slice(0, 12)}…` : null}
                              {e.chapter_id ? ` • chapter:${String(e.chapter_id).slice(0, 12)}…` : null}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="inline-flex items-center gap-2">
                            <button
                              type="button"
                              className="inline-flex items-center justify-center h-9 w-9 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200"
                              onClick={() => setSelectedEvent(e)}
                              title="Открыть details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              className="inline-flex items-center justify-center h-9 w-9 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200"
                              onClick={() => copyText(e.id)}
                              title="Скопировать id события"
                            >
                              <Copy className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="p-4 flex items-center justify-between gap-3 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
              <div className="text-sm text-gray-500 dark:text-gray-300">
                {hasNextPage ? 'Есть ещё события…' : 'Конец списка'}
              </div>
              <div className="flex items-center gap-2">
                {hasNextPage ? (
                  <Button
                    variant="secondary"
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                  >
                    {isFetchingNextPage ? 'Загружаю…' : 'Загрузить ещё'}
                  </Button>
                ) : null}
              </div>
            </div>
            <div ref={sentinelRef} />
          </div>
        ) : (
          <div className="text-sm text-gray-500 dark:text-gray-300">Событий не найдено.</div>
        )}
      </Card>

      <Modal
        isOpen={Boolean(selectedEvent)}
        onClose={() => setSelectedEvent(null)}
        title={selectedEvent ? `${getAuditLabel(selectedEvent)} — ${formatDateTimeRu(String(selectedEvent.created_at))}` : 'Details'}
        size="xl"
        footer={
          selectedEvent ? (
            <>
              <Button
                variant="secondary"
                onClick={() => copyText(prettyJson(selectedEvent.details))}
              >
                Скопировать details
              </Button>
              <Button variant="secondary" onClick={() => copyText(selectedEvent.id)}>
                Скопировать id
              </Button>
              <Button variant="primary" onClick={() => setSelectedEvent(null)}>
                Закрыть
              </Button>
            </>
          ) : undefined
        }
      >
        {selectedEvent ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-700">
                <div className="text-xs text-gray-500 dark:text-gray-400">Action</div>
                <div className="mt-1 text-gray-900 dark:text-white font-mono break-all">{selectedEvent.action}</div>
              </div>
              <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-700">
                <div className="text-xs text-gray-500 dark:text-gray-400">Actor</div>
                <div className="mt-1 text-gray-900 dark:text-white break-all">{getActorLabel(selectedEvent)}</div>
              </div>
              <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-700">
                <div className="text-xs text-gray-500 dark:text-gray-400">Entity</div>
                <div className="mt-1 text-gray-900 dark:text-white font-mono break-all">
                  {selectedEvent.entity_type}:{selectedEvent.entity_id}
                </div>
              </div>
              <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-700">
                <div className="text-xs text-gray-500 dark:text-gray-400">Links</div>
                <div className="mt-1 flex flex-wrap gap-2">
                  {selectedEvent.job_id ? (
                    <Link to={`/jobs/${selectedEvent.job_id}`} className="text-primary-700 dark:text-primary-300 hover:underline">
                      Job
                    </Link>
                  ) : null}
                  {selectedEvent.series_id ? (
                    <Link to={`/series/${selectedEvent.series_id}`} className="text-primary-700 dark:text-primary-300 hover:underline">
                      Series
                    </Link>
                  ) : null}
                </div>
              </div>
            </div>

            <div>
              <div className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Details (JSON)</div>
              <pre className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg overflow-auto text-xs border border-gray-200 dark:border-gray-700">
                {prettyJson(selectedEvent.details)}
              </pre>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
